/*
    Merrid Controls. Wszelkie prawa zastrzeżone.
    Plik zawiera kody Źródłowe będące własnością firmy Merrid Controls.
    Nie wolno ich rozpowszechniać w całości lub fragmentach bez wyraźnej zgody właściciela.
*/


import React, {useContext, useEffect, useRef, useState} from "react";

import {useTypedSelector} from "@Services/Hooks/UseTypedSelector";

import APPLICATION_ROUTER_INTERFACE from "@ROUTER/Interfaces/Router";
import ApplicationRouterContext from "@Contexts/Router";

import LOGGED_USER_INTERFACE from "@Store/Interfaces/Authentication/User";


import {Redirect} from "react-router-dom";


import BlockContentHeaderComponent from "@Components/UI/Containers/Block/Header";
import BlockContentContainerComponent from "@Components/UI/Containers/Block";
import {useDispatch} from "react-redux";
import ApplicationApiContext from "@Contexts/Api";
import {TERMINAL_INTERFACE} from "@Services/Application/Interfaces/Terminals/Terminal";
import {ERROR_SYSTEM_MESSAGE_INTERFACE} from "@Services/Application/Interfaces/_Messages/Errors";
import VEHICLE_INTERFACE from "@Services/Application/Interfaces/Vehicles/Vehicle";
import CHANGE_API_REQUEST_STATUS_ACTION from "@Actions/Interface/ChangeApiRequestStatus";
import {HTTP_ERROR_RESPONSE_INTERFACE} from "@Services/Interfaces/Http/Response/Error";
import DEFAULT_REQUEST_HEADERS from "@Services/Interfaces/Http/Request/Headers";
import ErrorMessageComponent from "@Components/UI/Communicates/Messages/Error";
import {Button, Col, Form, Row} from "react-bootstrap";
import FavouriteVehiclesVehicleComponent from "@UIPages/Authenticated/Settings/Favorite/Vehicles/Vehicle";
import VehiclesActiveSetComponent from "@UIPages/Authenticated/Settings/Favorite/Vehicles/ActiveSet";
import AUTHORIZATION_TOKEN_INTERFACE from "@Reducers/Api/RequestToken";
import REST_API_REQUEST from "@Services/Utilities/Requests";
import prepareRestApiRequestToken from "@Services/Utilities/RequestToken/Prepare";


type MyFavouriteVehiclesPageProps = {};


export const SettingsMyFavouriteVehiclesPageComponent: React.FC<MyFavouriteVehiclesPageProps> = (): JSX.Element | null => {


    /** @const Global State Actions Dispatcher */
    const DISPATCHER = useDispatch();


    /** @const {React.Ref} My Favourite Terminals Page Container DOM Ref */
    const COMPONENT_REF: React.Ref<HTMLDivElement | null> = useRef(null);


    /** @const {UserInterface | null} Application Logged User (from Global Store Object) */
    const LOGGED_USER: LOGGED_USER_INTERFACE = useTypedSelector(
        (state): ReturnType<any> => state?.authentication
    )?.loggedUser || null;


    /** @const {string} Global Rest Api Root URL Address */
    const REST_API_URL = useContext(ApplicationApiContext)?.API_ROOT_URL || null;

    /** @const {object} REST_API_DATA - Application State Store Rest Api Data */
    const REST_API_DATA:ReturnType<any> = useTypedSelector(
        (state) => state.api
    );

    /** @const {} REST_API_REQUEST_TOKEN - Application Rest Api Request Token */
    const REST_API_REQUEST_TOKEN: AUTHORIZATION_TOKEN_INTERFACE = REST_API_DATA?.requestToken || null;


    /** @const {APPLICATION_ROUTER_INTERFACE} Global Router Object */
    const ROUTER: APPLICATION_ROUTER_INTERFACE = useContext(ApplicationRouterContext);


    /** @var {[object, function]} State - Vehicles List Object, Vehicles List Object Setter */
    const [VEHICLES_LIST, SET_VEHICLES_LIST] = useState<VEHICLE_INTERFACE[]>([]);
    /** @var {[boolean, function]} State - Vehicles List Component Error, Vehicles List Component Error Setter */
    const [VEHICLES_LIST_ERROR, SET_VEHICLES_LIST_ERROR] = useState<ERROR_SYSTEM_MESSAGE_INTERFACE | null>(null);

    const [VEHICLES_LIST_UPDATE_TRIGGER, SET_VEHICLES_LIST_UPDATE_TRIGGER] = useState<boolean>(false);

    /** @hook {didMount, didUpdate} Get Vehicles List from REST API */
    useEffect(() => {

        if (VEHICLES_LIST_UPDATE_TRIGGER) SET_VEHICLES_LIST_UPDATE_TRIGGER(false);

        if (VEHICLES_LIST.length > 0 && !VEHICLES_LIST_UPDATE_TRIGGER) return;

        const VEHICLES_LIST_API_URL: string = REST_API_URL + "/Vehicle/List";

        const VEHICLES_LIST_REQUEST_DATA = {
            UserId: LOGGED_USER?.userID, // Logged User ID
            Mode: "A" // Data Return Mode - Simplified
        };

        const GET_VEHICLES_LIST = async (): Promise<void | Function> => {

            try {

                const VEHICLES_LIST_RESPONSE = await REST_API_REQUEST(
                    VEHICLES_LIST_API_URL,
                    VEHICLES_LIST_REQUEST_DATA,
                    REST_API_REQUEST_TOKEN,
                );

                if (VEHICLES_LIST_RESPONSE.status === 200) {
                    const CONTENT: {Vehicles: VEHICLE_INTERFACE[]} = await VEHICLES_LIST_RESPONSE.json();
                    SET_VEHICLES_LIST(CONTENT?.Vehicles);
                } else {
                    if (VEHICLES_LIST_RESPONSE.status === 409) {
                        const CONTENT: HTTP_ERROR_RESPONSE_INTERFACE = await VEHICLES_LIST_RESPONSE.json();
                        SET_VEHICLES_LIST_ERROR({ErrorCode: CONTENT.ErrorCode, UserMessage: CONTENT.UserMessage});
                    } else {
                        SET_VEHICLES_LIST_ERROR({ErrorCode: VEHICLES_LIST_RESPONSE.status, UserMessage: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"});
                    }
                }

            } catch (e) {
                SET_VEHICLES_LIST_ERROR({ErrorCode: 500, UserMessage: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"});
            }

        };

        GET_VEHICLES_LIST();

    }, [VEHICLES_LIST_UPDATE_TRIGGER]);


    useEffect(() => {
        return () => {
            SET_VEHICLES_LIST([]);
            SET_VEHICLES_LIST_ERROR(null);
            SET_VEHICLES_LIST_UPDATE_TRIGGER(false);
        };
    }, []);


    const [SELECTED_VEHICLE, SET_SELECTED_VEHICLE] = useState<string>("");


    /** Check if User is Logged */
    if (!LOGGED_USER || !LOGGED_USER?.userID) return <Redirect to={"/logowanie"}/>; // User is not logged => Redirect to LOGIN Page


    /** Check if Router is present */
    if (!ROUTER) return <Redirect to={"/404"}/>; // No Router Object - Redirect to 404 Page


    if (!VEHICLES_LIST) return null;


    const FAVORITE_VEHICLES_LIST = VEHICLES_LIST.filter((VEHICLE) => !!VEHICLE.Favorite);
    const OTHER_VEHICLES_LIST = VEHICLES_LIST.filter((VEHICLE) => !VEHICLE.Favorite);


    const REMOVE_FROM_FAVORITES_HANDLER = async (VEHICLE: VEHICLE_INTERFACE | null): Promise<void> => {

        if (!VEHICLE) return;

        let TOKEN = REST_API_REQUEST_TOKEN;

        if (VEHICLE.Active) {
            TOKEN = await VEHICLE_ACTIVE_STATUS_HANDLER(VEHICLE, "OFF", "OFF");
        }

        try {

            const VEHICLES_LIST_RESPONSE = await REST_API_REQUEST(
                `${REST_API_URL}/Vehicle/Favorite`,
                {UserId: LOGGED_USER?.userID, RegNo: VEHICLE.RegNo},
                TOKEN,
                "DELETE"
            );

            if (VEHICLES_LIST_RESPONSE.status === 204) {

                const UPDATED_VEHICLES_LIST = VEHICLES_LIST.filter((V) => V.RegNo !== VEHICLE.RegNo);
                SET_VEHICLES_LIST_UPDATE_TRIGGER(true);

            } else {

                const CONTENT: HTTP_ERROR_RESPONSE_INTERFACE = await VEHICLES_LIST_RESPONSE.json();
                SET_VEHICLES_LIST_ERROR({ErrorCode: VEHICLES_LIST_RESPONSE.status, UserMessage: CONTENT?.UserMessage});

            }

        } catch (e) {
            SET_VEHICLES_LIST_ERROR({ErrorCode: 500, UserMessage: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"});
        }

    }


    const ADD_TO_FAVORITES_HANDLER = async (VEHICLE_REG_NO: string): Promise<void> => {

        if (!VEHICLE_REG_NO) return;

        try {

            const VEHICLES_LIST_RESPONSE = await REST_API_REQUEST(
                `${REST_API_URL}/Vehicle/Favorite`,
            {UserId: LOGGED_USER?.userID, RegNo: VEHICLE_REG_NO},
                REST_API_REQUEST_TOKEN
            );

            if (VEHICLES_LIST_RESPONSE.status === 204) {

                SET_SELECTED_VEHICLE("");
                SET_VEHICLES_LIST_UPDATE_TRIGGER(true);

            } else {

                const CONTENT: HTTP_ERROR_RESPONSE_INTERFACE = await VEHICLES_LIST_RESPONSE.json();
                SET_VEHICLES_LIST_ERROR({ErrorCode: VEHICLES_LIST_RESPONSE.status, UserMessage: CONTENT?.UserMessage});

            }

        } catch (e) {
            SET_VEHICLES_LIST_ERROR({ErrorCode: 500, UserMessage: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"});
        }

    }


    const VEHICLE_ACTIVE_STATUS_HANDLER = async (VEHICLE: VEHICLE_INTERFACE | null, ACTION: "ON" | "OFF", TRIGGER: "ON" | "OFF" = "ON", TOKEN?: any): Promise<any> => {

        if (!VEHICLE) return;

        const CHANGE_VEHICLE_ACTIVE_STATUS_REQUEST_METHOD = ACTION === "OFF" ? "DELETE" : "POST";

        const CHANGE_VEHICLE_ACTIVE_STATUS_REQUEST_DATA = {
            UserId: LOGGED_USER?.userID,
            RegNo: VEHICLE.RegNo,
            IsFront: !!VEHICLE.IsFront
        };

        try {

            const VEHICLES_LIST_RESPONSE = await REST_API_REQUEST(
                `${REST_API_URL}/Vehicle/Active`,
                CHANGE_VEHICLE_ACTIVE_STATUS_REQUEST_DATA,
                TOKEN || REST_API_REQUEST_TOKEN,
                CHANGE_VEHICLE_ACTIVE_STATUS_REQUEST_METHOD
            );

            if (VEHICLES_LIST_RESPONSE.status === 204) {

                if (TRIGGER === "ON") {
                    SET_VEHICLES_LIST_UPDATE_TRIGGER(true);
                }

                SET_VEHICLES_LIST_ERROR(null);

            } else {

                const CONTENT: HTTP_ERROR_RESPONSE_INTERFACE = await VEHICLES_LIST_RESPONSE.json();
                SET_VEHICLES_LIST_ERROR({ErrorCode: VEHICLES_LIST_RESPONSE.status, UserMessage: CONTENT?.UserMessage});

            }

            return prepareRestApiRequestToken(VEHICLES_LIST_RESPONSE.headers);

        } catch (e) {
            SET_VEHICLES_LIST_ERROR({ErrorCode: 500, UserMessage: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"});
        }

    }

    const FavoriteVehiclesList = FAVORITE_VEHICLES_LIST.map((VEHICLE, i: number) =>
        <FavouriteVehiclesVehicleComponent
            key={i}
            VEHICLE={VEHICLE}
            REMOVE_FROM_FAVORITES_HANDLER={REMOVE_FROM_FAVORITES_HANDLER}
        />
    );


    const OtherVehiclesList = OTHER_VEHICLES_LIST.map((VEHICLE, i: number) =>
        <option key={i} value={VEHICLE.RegNo}>{VEHICLE.RegNo}</option>
    );


    return (
        <BlockContentContainerComponent forwardRef={COMPONENT_REF} cssClasses={"settings-page-information-data-component vehicles-page-component settings-page-view-component"}>

            <BlockContentHeaderComponent headerText={"Ulubione pojazdy"} />

            <>{!!VEHICLES_LIST_ERROR && <ErrorMessageComponent messageText={VEHICLES_LIST_ERROR?.UserMessage} />}</>

            {!VEHICLES_LIST_ERROR &&
            <VehiclesActiveSetComponent
                FAVORITE_VEHICLES={FAVORITE_VEHICLES_LIST}
                ACTIVE_VEHICLE_STATUS_HANDLER={VEHICLE_ACTIVE_STATUS_HANDLER}
            />}


            {!VEHICLES_LIST_ERROR &&
	        <Col xs={12} className={"default-section-header"}>
		        <span>{`Ulubione`}</span>
	        </Col>
            }

            {!VEHICLES_LIST_ERROR && OtherVehiclesList.length > 0 &&
	        <Col xs={12} className={"vehicles-list-add-component vehicles-list-add other-vehicles-list"}>
		        <Row className={"vehicles-list-content"}>
			        <Col xs={5} className={"vehicles-list-add-choice"}>
				        <Form.Control as={"select"} value={SELECTED_VEHICLE} onChange={(e: any) => SET_SELECTED_VEHICLE(e.target.value)}>
					        <option value={""}>{`wybierz`}</option>
                            {OtherVehiclesList}
				        </Form.Control>
			        </Col>

			        <Col xs={7} className={"vehicles-list-add-submit"}>
				        <Button disabled={!SELECTED_VEHICLE} onClick={() => ADD_TO_FAVORITES_HANDLER(SELECTED_VEHICLE)}>{`Dodaj do ulubionych`}</Button>
			        </Col>

		        </Row>
	        </Col>
            }

            {!VEHICLES_LIST_ERROR &&
            <Col xs={12} className={"vehicles-list-component vehicles-list favorite-vehicles-list"}>
                <Row className={"vehicles-list-content"}>

                    {FavoriteVehiclesList.length < 1 ?
                    <Col xs={12} className={"no-list-elements-message default-system-message"}>
                        <span>{`Brak ulubionych pojazdów`}</span>
                    </Col>
                    :
                    FavoriteVehiclesList
                    }

                </Row>
            </Col>
            }

        </BlockContentContainerComponent>
    );

};


export default SettingsMyFavouriteVehiclesPageComponent;
